import { z } from 'zod';
import { uuidSchema } from './common.schema.js';

/**
 * Shape of an ENTERPRISE item stored in DynamoDB.
 *
 * Key conventions (dev-tms-phoenix-enterprise table):
 *   pk  = "ENTERPRISE#<enterprise_id>"
 *   sk  = "METADATA"
 */
export const EnterpriseDynamoSchema = z.object({
  enterprise_id: uuidSchema,

  enterprise_name: z.string().min(1),

  enterprise_type: z.enum([
    'HOSPITAL_GROUP',
    'NHS_TRUST',
    'INDEPENDENT',
    'CORPORATE',
    'CLINICAL',
    'NON_CLINICAL',
    'SAAS_ADMIN',
  ]).nullable().optional(),

  registration_number: z.string().optional(),
  vat_number: z.string().optional(),
  address_line1: z.string().optional(),
  city: z.string().optional(),
  country_code: z.string().length(2).optional(),
  timezone: z.string().optional(),
  locale: z.string().optional(),

  // GDPR / DPO
  data_controller_name: z.string().optional(),
  data_controller_email: z.string().email().optional(),
  dpo_name: z.string().optional(),
  dpo_email: z.string().email().optional(),

  is_active: z.boolean().default(true),

  created_at: z.string(),
  updated_at: z.string(),
});

export type EnterpriseDynamo = z.infer<typeof EnterpriseDynamoSchema>;

/** Response schema — same shape as the DynamoDB item. */
export const EnterpriseResponseSchema = EnterpriseDynamoSchema;
export type EnterpriseResponse = z.infer<typeof EnterpriseResponseSchema>;

/**
 * POST /api/v1/enterprises — create a new enterprise.
 * `enterprise_id`, `created_at`, `updated_at` are server-generated.
 */
export const CreateEnterpriseSchema = EnterpriseDynamoSchema.omit({
  enterprise_id: true,
  created_at: true,
  updated_at: true,
});
export type CreateEnterprise = z.infer<typeof CreateEnterpriseSchema>;

/**
 * PUT /api/v1/enterprises/:enterprise_id — full or partial update.
 * All fields optional except the path param (supplied separately).
 * `enterprise_id`, `hospital_id`, `created_at`, `updated_at` are immutable.
 */
export const UpdateEnterpriseSchema = EnterpriseDynamoSchema.omit({
  enterprise_id: true,
  created_at: true,
  updated_at: true,
}).partial();
export type UpdateEnterprise = z.infer<typeof UpdateEnterpriseSchema>;
